import React, { useEffect, useRef, useMemo, useImperativeHandle, forwardRef } from "react";
import { toODataString } from "@progress/kendo-data-query";
import moment from "moment";
import { process } from "@progress/kendo-data-query";

const DataLoader = forwardRef((props, ref) => {
  const {
    gridRef,
    core,
    parentDiv,
    autorefresh,
    url,
    urlPostParams,
    dataState,
    columnConfig,
    filterLogic,
    forceDefaultFilters,
    gridDefaultFilters,
    onDataRecieved,
    notif,
    appId,
    setIsFetching,
    isFetchingRef,
  } = props;

  const init = { method: "GET", accept: "application/json", headers: {} };
  const lastSuccess = useRef(undefined);
  const pending = useRef(undefined);
  const lastGroup = useRef(null);
  const triggerGetCallRef = useRef();
  const lastResponseData = useRef(null);
  const currentController = useRef(null);
  const currentOrigin = useRef(null);
  const autoRefreshTimer = useRef(null);

  const dataStateKey = useMemo(() => {
    const { group, ...rest } = dataState;
    return JSON.stringify(rest);
  }, [dataState]);
  const loader = core.make("oxzion/splash");

  const reprocessDataWithGroup = () => {
    if (lastResponseData.current) {
      const groupConfig = dataState.group ? { group: dataState.group } : null;
      onDataRecieved.call(undefined, {
        data: groupConfig ? process(lastResponseData.current, groupConfig).data : lastResponseData.current,
        total: lastResponseData.current?.total ?? lastResponseData.current?.length ?? 0,
      });
    }
  };

  const triggerGetCall = (origin) => {
    if (origin === "debounced" && currentOrigin.current === "autorefresh" && currentController.current) {
      currentController.current.abort();
      if (autoRefreshTimer.current) {
        clearInterval(autoRefreshTimer.current);
        autoRefreshTimer.current = null;
      }
    }
    isFetchingRef.current = true;
    setIsFetching(true);
    pending.current = toODataString(dataState);
    getData(url, origin)
      .then((response) => {
        lastSuccess.current = toODataString(dataState);
        pending.current = undefined;
        if (toODataString(dataState) === lastSuccess.current) {
          if (typeof response === "object" && response.status === "success") {
            const groupConfig = dataState.group ? { group: dataState.group } : null;
            const finalData = response.data?.data ?? response.data;
            lastResponseData.current = finalData;
            onDataRecieved.call(undefined, {
              data: groupConfig ? process(finalData, groupConfig).data : finalData,
              total: finalData?.total ?? response.total ?? null,
            });
          }
        } else {
          requestDataIfNeeded("triggerGetCall");
        }
      })
      .catch((err) => {
        console.error("API error:", err);
      })
      .finally(() => {
        isFetchingRef.current = false;
        loader.destroy();
        setIsFetching(false);
        if (origin === "debounced" && !autoRefreshTimer.current && autorefresh?.enabled) {
          setTimeout(() => {
            initAutoRefresh();
          }, 3000);
        }
      });
  };

  triggerGetCallRef.current = triggerGetCall;
  const debouncedTriggerGetCall = useMemo(
    () => core.make("oxzion/search")?.delayedSearch((...args) => triggerGetCallRef.current("debounced", ...args), 1000),
    [],
  );

  useImperativeHandle(
    ref,
    () => ({
      triggerGetCall,
      prepareQueryFilters,
    }),
    [triggerGetCall, prepareQueryFilters],
  );

  const prepareAPIRoute = (url) => {
    let paramSeperator = url !== undefined ? (url.includes("?") ? "&" : "?") : "&";
    var passColumnConfig = typeof props.passColumnConfig == "string" ? props.passColumnConfig.split(",") : props.passColumnConfig;
    var columnList = passColumnConfig
      ? passColumnConfig.concat(
          columnConfig.map((item) => (item.field ? item.field : null)).filter((el) => el != null && passColumnConfig.indexOf(el) < 0),
        )
      : columnConfig.map((item) => (item.field ? item.field : null));
    if (Object.keys(dataState).length === 0) {
      return columnList ? url + paramSeperator + "columns=" + JSON.stringify(columnList) : url;
    } else {
      var filterConfig = prepareQueryFilters(dataState);
      var baseFilter = "filter=[" + encodeURIComponent(JSON.stringify(filterConfig)) + "]";
      var columnsParam = columnList ? "&columns=" + encodeURIComponent(JSON.stringify(columnList)) : "";
      var finalRoute = url + paramSeperator + baseFilter + columnsParam;
      return finalRoute;
    }
  };

  const initAutoRefresh = () => {
    if (autorefresh?.enabled && autorefresh?.config != "") {
      // Clear existing timer
      if (autoRefreshTimer.current) {
        clearInterval(autoRefreshTimer.current);
      }
      autoRefreshTimer.current = setInterval(() => {
        var gridElement = document.getElementById(parentDiv);
        if (gridElement) {
          const isVisible = !document.hidden && (gridElement.offsetWidth || gridElement.offsetHeight || gridElement.getClientRects().length > 0);
          const appElement = document.getElementById(`navigation_${appId}`).closest("[data-focused]");
          const isAppFocused = new Function("return " + appElement?.getAttribute("data-focused"))();

          if (isVisible && !isFetchingRef.current && isAppFocused) {
            triggerGetCallRef.current("autorefresh");
            const event = new CustomEvent("clearEvalCache", {
              bubbles: true,
            });
            document.getElementById(parentDiv)?.dispatchEvent(event);
          }
        } else {
          if (autoRefreshTimer.current) {
            clearInterval(autoRefreshTimer.current);
            autoRefreshTimer.current = null;
          }
        }
      }, (autorefresh.config > 4 ? autorefresh.config : 5) * 1000);
    }
  };

  const getData = async (url, origin) => {
    if (currentController.current && currentOrigin.current !== origin) {
      currentController.current.abort();
    }
    const controller = new AbortController();
    currentController.current = controller;
    currentOrigin.current = origin;
    try {
      if (typeof core == "undefined") {
        let response = await fetch(url, { ...init, signal: currentController.current.signal });
        let json = await response.json();
        if (json.status == "success") {
          let data = { data: json.value, total: json["@odata.count"] };
          return data;
        } else {
          let data = { data: [], total: 0 };
          return data;
        }
      } else {
        let helper = core.make("oxzion/restClient");
        var route = prepareAPIRoute(url);
        let data = urlPostParams
          ? await helper.request("v1", "/" + route, urlPostParams, "post", {}, false, 1, controller.signal)
          : await helper.request("v1", "/" + route, {}, "get", {}, false, 1, controller.signal);

        if (data.status == "success") {
          return data;
        } else {
          notif?.current?.notify("Error", "Invalid search or filter input. Please try again with different input.", "danger");
          return { data: [], total: 0 };
        }
      }
    } finally {
      if (currentController.current === controller) {
        currentController.current = null;
        currentOrigin.current = null;
      }
    }
  };

  const prepareQueryFilters = (filterConfig) => {
    var gridConfig = JSON.parse(JSON.stringify(filterConfig));
    if (forceDefaultFilters) {
      try {
        if (gridConfig.sort.length == 0 || gridConfig.sort == null) {
          gridConfig.sort = gridDefaultFilters.sort ? gridDefaultFilters.sort : gridConfig.sort;
        }
      } catch {}
    }
    if (filterLogic && gridConfig.filter) {
      gridConfig.filter.logic = filterLogic;
    }
    columnConfig.map((ColumnItem) => {
      if (ColumnItem.filter && ColumnItem.filter == "date") {
        ColumnItem.filterFormat = "YYYY-MM-DD";
      }
      if (ColumnItem.filterFormat && gridConfig.filter) {
        gridConfig.filter.filters?.map((filterItem1, i) => {
          filterItem1.filters &&
            filterItem1.filters.map((filterItems1, j) => {
              processFilters(filterItems1, ColumnItem, gridConfig, i, j, filterItem1);
            });
          if (filterItem1) processFilters(filterItem1, ColumnItem, gridConfig, i, undefined, true);
        });
      }
      if (ColumnItem.multiFieldFilter && gridConfig.filter) {
        var newFilters = [];
        gridConfig.filter.filters.map((filterItem2, i) => {
          if (filterItem2.field == ColumnItem.field) {
            var mergeFilterArray = [filterItem2];
            ColumnItem.multiFieldFilter.map((multiFieldItem) => {
              let filterCopy = JSON.parse(JSON.stringify(filterItem2));
              mergeFilterArray.push({
                field: multiFieldItem,
                operator: filterCopy.operator,
                value: filterCopy.value,
              });
            });
            var mergeFilter = {
              filter: {
                logic: "or",
                filters: mergeFilterArray,
              },
            };
            newFilters.push(mergeFilter);
          } else {
            newFilters.push(filterItem2);
          }
        });
        gridConfig.filter.filters = newFilters;
      }
    });
    return gridConfig;
  };

  const requestDataIfNeeded = (origin) => {
    const currentGroup = JSON.stringify(dataState?.group || []);

    // Check if we should skip this request
    if (currentGroup === lastGroup.current) {
      // Allow debounced calls to proceed even if there's a pending request
      const isDebouncedCall = lastSuccess.current && origin !== "triggerGetCall";

      if (!isDebouncedCall && ((pending.current && pending.current != undefined) || toODataString(dataState) === lastSuccess.current)) {
        return;
      }
    }
    lastGroup.current = currentGroup;

    // Always use "debounced" origin for debounced calls
    if (lastSuccess.current && origin !== "triggerGetCall") {
      debouncedTriggerGetCall();
    } else {
      triggerGetCall(origin);
    }
  };

  const processFilters = (filterItem, ColumnItem, gridConfig, i, j, filterItem1) => {
    if (filterItem.field == ColumnItem.field) {
      var result = moment(filterItem.value).format(ColumnItem.filterFormat);
      if (filterItem.value && result != "Invalid date" && filterItem1) {
        filterItem1.filters ? (gridConfig.filter.filters[i].filters[j].value = result) : (gridConfig.filter.filters[i].value = result);

        return gridConfig;
      }
    }
  };

  useEffect(() => {
    setTimeout(() => {
      const oxGridElement = gridRef?.current?.querySelector(".oxGrid");
      oxGridElement && loader.show(oxGridElement);
    }, 0);

    isFetchingRef.current = false;
    initAutoRefresh();

    return () => {
      if (autoRefreshTimer.current) {
        clearInterval(autoRefreshTimer.current);
      }
      if (currentController.current) {
        currentController.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const currentGroup = JSON.stringify(dataState?.group || []);
    if (currentGroup !== lastGroup.current && lastResponseData.current) {
      lastGroup.current = currentGroup;
      reprocessDataWithGroup();
    }
  }, [dataState.group]);

  useEffect(() => {
    requestDataIfNeeded("dataStateChange");
  }, [dataStateKey, url, urlPostParams]);

  return <div></div>;
});

export default DataLoader;
